import React, { useState, useEffect } from 'react';
import { Skeleton, Box } from '@mui/material';
import { useTheme } from '@mui/system';

interface CollageProps {
  collageImage: string;
  collageImageSmall: string;
}

const CollageImage: React.FC<CollageProps> = ({
  collageImage,
  collageImageSmall
}) => {
  const theme = useTheme();
  const [isSmallScreen, setIsSmallScreen] = useState<boolean | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
  }, [collageImage]);

  useEffect(() => {
    // Detect screen size on client after hydration
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 600);
    };

    // Set initial screen size
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Define dimensions for small and large images
  const smallImageDimensions = { width: 600, height: 450 };
  const largeImageDimensions = { width: 1600, height: 1200 };

  // Decide on dimensions based on screen size
  const dimensions = isSmallScreen
    ? smallImageDimensions
    : largeImageDimensions;

  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box
      margin="0 auto"
      sx={{
        maxWidth: {
          xs: '100%',
          sm: '80%'
        }
      }}
    >
      <Box
        style={{
          position: 'relative',
          width: '100%',
          paddingBottom: `${(dimensions.height / dimensions.width) * 100}%` // Maintain aspect ratio
        }}
      >
        {!imageLoaded && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="wave"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              borderRadius: 2, // Adds slight rounding for better aesthetics
              boxShadow: 2 // Adds subtle shadow
            }}
          />
        )}

        {/* Only show the image after hydration */}
        {isSmallScreen !== null && (
          <Box
            component="img"
            src={isSmallScreen ? collageImageSmall : collageImage}
            alt=""
            width={dimensions.width}
            height={dimensions.height}
            id="primaryimage"
            role="presentation"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: imageLoaded ? 'block' : 'none', // Hide the image until it's loaded
              opacity: isDarkMode ? '0.9' : '1', // Fade in the image
              borderRadius: 2, // Adds slight rounding for better aesthetics
              boxShadow: 2 // Adds subtle shadow
            }}
            onLoad={handleImageLoad}
          />
        )}
      </Box>
    </Box>
  );
};

export default CollageImage;
